import { useState } from 'react';
import { FileText, ShieldCheck, Check, Upload, AlertCircle, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import {
  PageHeader, Card, CardHeader, EmptyState, Badge, Button, Field, Select,
  ConfirmDialog, useToast, cn,
} from '@/components/ui';
import { DocumentList, FileUploader } from '@/components/Files';
import { usePortal } from './PortalShell';
import type { TenantDocumentKind } from '@shared/types';

/**
 * The tenant's paperwork — what the landlord has shared down, and what the
 * tenant has sent up.
 *
 * ── Why the tenant can upload at all ──────────────────────────────────────
 *
 * Because otherwise every ID check is a photograph sent by text message to a
 * personal phone, which is worse for everybody: the landlord has to file it by
 * hand, and a passport ends up in a chat thread nobody can delete.
 *
 * The checklist is the important half. A tenant who can see exactly what is
 * still outstanding generally sends it; a tenant who has only ever been told
 * "we need some documents" generally does not.
 *
 * A tenant who cannot or will not do this loses nothing — their manager can
 * still upload on their behalf, and that path was never removed.
 */
export default function PortalDocuments() {
  const { data, reload } = usePortal();
  const toast = useToast();

  const missing = data.required_documents.filter((d) => !d.held);
  const [kind, setKind] = useState<TenantDocumentKind>(
    missing.find((m) => m.uploadable)?.kind ?? data.uploadable_kinds[0]?.kind ?? 'other',
  );
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const contracts = data.documents.filter((d) => d.kind === 'contract');
  const receipts = data.documents.filter((d) => d.kind === 'payment_proof');
  const mine = data.documents.filter((d) => d.mine);
  const shared = data.documents.filter(
    (d) => !d.mine && !['contract', 'payment_proof'].includes(d.kind),
  );

  const upload = async (payload: { filename: string; mime_type: string; data: string }) => {
    await api.post('/portal/documents', { ...payload, kind });
    reload();
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.del(`/portal/documents/${removing}`);
      toast.success('Document removed');
      reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'That could not be removed');
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        subtitle="Your agreement, your receipts, and anything your landlord needs from you"
      />

      {/* ── What is still outstanding ───────────────────────────────────── */}
      {data.required_documents.length > 0 && (
        <Card>
          <CardHeader
            title={missing.length === 0 ? 'You are up to date' : 'What your landlord still needs'}
            subtitle={
              missing.length === 0
                ? `${data.org.name} has everything they asked for.`
                : 'Send these when you can. It saves being chased for them.'
            }
          />
          <ul className="px-5 sm:px-6 pb-5 space-y-2">
            {data.required_documents.map((doc) => (
              <li key={doc.kind} className="flex items-center gap-3 text-sm">
                <span
                  className={cn(
                    'shrink-0 h-5 w-5 rounded-full flex items-center justify-center',
                    doc.held ? 'bg-ok/15 text-ok' : 'bg-warn/15 text-warn',
                  )}
                  aria-hidden
                >
                  {doc.held ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                </span>
                <span className={cn('flex-1', doc.held && 'text-muted')}>{doc.label}</span>
                {doc.held ? (
                  <Badge tone="ok">Received</Badge>
                ) : doc.uploadable ? (
                  <Badge tone="warn">Needed</Badge>
                ) : (
                  /*
                   * The agreement is the landlord's copy of something they
                   * issued, so there is no upload button for it. Saying so
                   * stops a tenant hunting for one.
                   */
                  <Badge tone="neutral">Your landlord adds this</Badge>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── Sending something in ────────────────────────────────────────── */}
      {data.can_upload ? (
        <Card>
          <CardHeader
            title="Send a document"
            subtitle="A clear photo is fine — you do not need a scanner."
          />
          <div className="p-5 sm:p-6 pt-4 space-y-4">
            <Field label="What is it?">
              <Select
                value={kind}
                onChange={(e) => setKind(e.target.value as TenantDocumentKind)}
              >
                {data.uploadable_kinds.map((k) => (
                  <option key={k.kind} value={k.kind}>{k.label}</option>
                ))}
              </Select>
            </Field>
            <FileUploader
              onUpload={upload}
              label="Take a photo, or choose a file"
              multiple={false}
            />
            <p className="text-xs text-faint">
              Only {data.org.name} can see what you send. You can remove anything
              you have uploaded yourself.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-5">
          <div className="flex items-start gap-3">
            <Upload className="h-5 w-5 text-faint shrink-0 mt-0.5" />
            <p className="text-sm text-muted leading-relaxed">
              Uploading is not switched on for this account yet. Send anything
              your landlord has asked for to them directly and they will add it
              for you.
            </p>
          </div>
        </Card>
      )}

      {/* ── Everything on file ──────────────────────────────────────────── */}
      {data.documents.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title="Nothing on file yet"
            message="When your landlord shares your agreement or other paperwork, it appears here for you to read at any time."
          />
        </Card>
      ) : (
        <>
          {contracts.length > 0 && (
            <Card>
              <CardHeader title="Agreements" subtitle="Your tenancy contract" />
              <div className="p-5 sm:p-6 pt-4">
                <DocumentList files={contracts} readOnly />
              </div>
            </Card>
          )}

          {mine.length > 0 && (
            <Card>
              <CardHeader
                title="What you have sent"
                subtitle="Uploaded by you. Only your landlord can see these."
              />
              <div className="p-5 sm:p-6 pt-4 space-y-2">
                <DocumentList files={mine} readOnly />
                <div className="flex flex-wrap gap-2 pt-2">
                  {mine.map((file) => (
                    <Button
                      key={file.id}
                      size="sm"
                      icon={<Trash2 className="h-3.5 w-3.5" />}
                      onClick={() => setRemoving(file.id)}
                    >
                      Remove {file.name}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {receipts.length > 0 && (
            <Card>
              <CardHeader title="Payment receipts" subtitle="Proof you have submitted" />
              <div className="p-5 sm:p-6 pt-4">
                <DocumentList files={receipts} readOnly />
              </div>
            </Card>
          )}

          {shared.length > 0 && (
            <Card>
              <CardHeader title="Shared with you" subtitle={`Added by ${data.org.name}`} />
              <div className="p-5 sm:p-6 pt-4">
                <DocumentList files={shared} readOnly />
              </div>
            </Card>
          )}
        </>
      )}

      <div className="flex items-start gap-3 p-4 rounded-2xl bg-raised border border-line">
        <ShieldCheck className="h-5 w-5 text-faint shrink-0 mt-0.5" />
        <p className="text-sm text-muted leading-relaxed">
          Only documents your landlord has chosen to share with you, and documents
          you have sent yourself, appear here — and only you can open them. They
          are not publicly linkable.
        </p>
      </div>

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        title="Remove this document?"
        message="It will be deleted. If your landlord still needs it you will have to send it again."
        confirmLabel="Remove"
      />
    </div>
  );
}
